import { useEffect, useState } from "react";
import { useI18n } from "@/i18n";
import { Download, Smartphone, X } from "lucide-react";

const isiOS = () =>
  typeof navigator !== "undefined" &&
  /iPad|iPhone|iPod/.test(navigator.userAgent) &&
  !window.MSStream;

const isStandalone = () =>
  (typeof window !== "undefined" &&
    window.matchMedia("(display-mode: standalone)").matches) ||
  (typeof navigator !== "undefined" && navigator.standalone === true);

const DISMISS_KEY = "ks_install_dismissed_at";
const DISMISS_COOLDOWN_DAYS = 30;
const FIRST_VISIT_KEY = "ks_first_visit_at";
const MIN_VISITS_BEFORE_PROMPT_MS = 3 * 24 * 60 * 60 * 1000; // 3 días

function shouldShow() {
  const t = localStorage.getItem(DISMISS_KEY);
  if (t) {
    const days = (Date.now() - parseInt(t, 10)) / (1000 * 60 * 60 * 24);
    if (days < DISMISS_COOLDOWN_DAYS) return false;
  }
  // Only after the user has been around for at least 3 days
  let first = localStorage.getItem(FIRST_VISIT_KEY);
  if (!first) {
    localStorage.setItem(FIRST_VISIT_KEY, String(Date.now()));
    return false;
  }
  return Date.now() - parseInt(first, 10) >= MIN_VISITS_BEFORE_PROMPT_MS;
}

export default function InstallPrompt() {
  const { t } = useI18n();
  const [deferred, setDeferred] = useState(null);
  const [showIOS, setShowIOS] = useState(false);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isStandalone() || !shouldShow()) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    if (isiOS()) {
      const t = setTimeout(() => {
        setShowIOS(true);
        setShow(true);
      }, 30000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onPrompt);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    setShow(false);
  };

  const dismiss = () => {
    setShow(false);
    setDeferred(null);
    setShowIOS(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  if (isStandalone() || !show) return null;

  if (deferred) {
    return (
      <div
        className="fixed bottom-3 left-3 right-3 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-xs z-40 fade-in"
        data-testid="install-prompt"
      >
        <div className="glass-card rounded-lg p-3 flex items-center gap-3 shadow-2xl">
          <Smartphone className="w-5 h-5 text-gold shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-gold-light text-xs font-medium">{t("install_title")}</div>
            <div className="text-[10px] text-faded">{t("install_sub")}</div>
          </div>
          <button
            onClick={install}
            className="text-xs bg-gold text-obsidian px-3 py-1.5 rounded font-medium hover:opacity-90"
            data-testid="install-btn"
          >
            {t("install_btn")}
          </button>
          <button onClick={dismiss} className="text-faded hover:text-muted shrink-0">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  }

  if (showIOS) {
    return (
      <div
        className="fixed bottom-3 left-3 right-3 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-xs z-40 fade-in"
        data-testid="install-prompt-ios"
      >
        <div className="glass-card rounded-lg p-3 relative shadow-2xl">
          <button
            onClick={dismiss}
            className="absolute top-2 right-2 text-faded hover:text-muted"
          >
            <X className="w-3.5 h-3.5" />
          </button>
          <div className="flex items-start gap-3">
            <Smartphone className="w-5 h-5 text-gold shrink-0 mt-0.5" />
            <div className="pr-3">
              <div className="text-gold-light text-xs font-medium mb-1">
                {t("install_ios_title")}
              </div>
              <div className="text-[10px] text-muted leading-relaxed">
                {t("install_ios_steps")}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
